export type TXsollaCheckoutErrorDetails = Record<string, unknown> | null;

export interface IXsollaCheckoutError {
  message: string;
  code?: string;
  statusCode?: number;
  details?: TXsollaCheckoutErrorDetails;
}

export type TStructuredXsollaCheckoutError = Error & {
  message: string;
  code: string;
  statusCode: number;
  details?: TXsollaCheckoutErrorDetails;
};

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function readNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function readDetails(value: unknown): TXsollaCheckoutErrorDetails | undefined {
  if (value === null) {
    return null;
  }

  return value && typeof value === 'object'
    ? value as Record<string, unknown>
    : undefined;
}

function getResponseBody(error: unknown): Record<string, unknown> | undefined {
  const response = error && typeof error === 'object' ? (error as any).response : undefined;

  if (response?.body && typeof response.body === 'object') {
    return response.body as Record<string, unknown>;
  }

  if (response?.data && typeof response.data === 'object') {
    return response.data as Record<string, unknown>;
  }

  return undefined;
}

export function createXsollaCheckoutError(params: {
  statusCode: number;
  code: string;
  message: string;
  details?: TXsollaCheckoutErrorDetails;
}): TStructuredXsollaCheckoutError {
  const error = new Error(params.message) as TStructuredXsollaCheckoutError;
  error.statusCode = params.statusCode;
  error.code = params.code;
  error.details = params.details ?? null;
  return error;
}

export function getXsollaCheckoutErrorStatusCode(error: unknown): number | undefined {
  const responseBody = getResponseBody(error);

  return readNumber((error as any)?.statusCode) ||
    readNumber((error as any)?.status) ||
    readNumber(responseBody?.statusCode) ||
    readNumber(responseBody?.status) ||
    readNumber((error as any)?.response?.statusCode) ||
    readNumber((error as any)?.response?.status);
}

export function getXsollaCheckoutErrorCode(error: unknown): string | undefined {
  const responseBody = getResponseBody(error);

  return readString(responseBody?.code) ||
    readString((error as any)?.code);
}

export function getXsollaCheckoutErrorMessage(error: unknown): string | undefined {
  const responseBody = getResponseBody(error);

  return readString(responseBody?.message) ||
    readString((error as any)?.message);
}

export function getXsollaCheckoutErrorDetails(error: unknown): TXsollaCheckoutErrorDetails | undefined {
  const responseBody = getResponseBody(error);

  return readDetails((error as any)?.details) ??
    readDetails(responseBody?.details);
}

export function toXsollaCheckoutError(error: unknown): IXsollaCheckoutError | null {
  const message = getXsollaCheckoutErrorMessage(error);
  const code = getXsollaCheckoutErrorCode(error);
  const statusCode = getXsollaCheckoutErrorStatusCode(error);
  const details = getXsollaCheckoutErrorDetails(error);

  if (!message && !code && statusCode === undefined && details === undefined) {
    return null;
  }

  return {
    message: message || '',
    code,
    statusCode,
    ...(details !== undefined ? { details } : {})
  };
}

export function isXsollaCheckoutError(error: unknown): error is TStructuredXsollaCheckoutError {
  const normalizedError = toXsollaCheckoutError(error);

  return !!normalizedError &&
    typeof normalizedError.message === 'string' &&
    typeof normalizedError.code === 'string' &&
    typeof normalizedError.statusCode === 'number';
}
